/**
 * API 클라이언트
 * 환경 변수 기반으로 Mock API와 실제 API를 전환합니다.
 * 모든 API 호출에 일관된 에러 처리 구조를 제공합니다.
 */

import type { ApiResponse, CreateNewsRequest, CreateGalleryRequest, UpdateNewsRequest, UpdateGalleryRequest } from './types';
import { mockStore } from '../../mocks/admin/store';

// 환경 변수 설정
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false'; // 기본값: true (Mock 사용)

// 토큰 저장 키
const TOKEN_KEY = 'admin_auth_token';

/**
 * 인증 토큰 저장
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 인증 토큰 조회
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 인증 토큰 삭제
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Mock API 요청 처리
 */
async function mockRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // 네트워크 지연 시뮬레이션 (200-500ms)
  await new Promise((resolve) => setTimeout(resolve, 200 + Math.random() * 300));

  const method = options.method || 'GET';
  const body = options.body ? JSON.parse(options.body as string) : null;

  try {
    // 인증 API
    if (endpoint === '/api/admin/login' && method === 'POST') {
      const result = mockStore.login(body.email, body.password);
      if (result) {
        return { success: true, data: result as T };
      }
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '이메일 또는 비밀번호가 올바르지 않습니다.',
        },
      };
    }

    if (endpoint === '/api/admin/logout' && method === 'POST') {
      mockStore.logout();
      return { success: true, data: { message: '로그아웃 되었습니다.' } as T };
    }

    if (endpoint === '/api/admin/me' && method === 'GET') {
      const user = mockStore.getCurrentUser();
      if (user) {
        return { success: true, data: { user } as T };
      }
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '인증이 필요합니다.',
        },
      };
    }

    // 뉴스 API
    if (endpoint.startsWith('/api/admin/news')) {
      return handleNewsEndpoint<T>(endpoint, method, body);
    }

    // 갤러리 API
    if (endpoint.startsWith('/api/admin/gallery')) {
      return handleGalleryEndpoint<T>(endpoint, method, body);
    }

    // 파일 업로드 API (Mock에서는 별도 처리)
    if (endpoint === '/api/admin/upload' && method === 'POST') {
      // Mock 업로드는 uploadService에서 직접 처리
      return { success: true, data: { url: '', filename: '' } as T };
    }

    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '요청한 API를 찾을 수 없습니다.',
      },
    };
  } catch {
    return {
      success: false,
      error: {
        code: 'SERVER_ERROR',
        message: '서버 오류가 발생했습니다.',
      },
    };
  }
}

/**
 * 뉴스 엔드포인트 처리
 */
function handleNewsEndpoint<T>(
  endpoint: string,
  method: string,
  body: Record<string, unknown> | null
): ApiResponse<T> {
  // URL에서 ID 추출
  const idMatch = endpoint.match(/\/api\/admin\/news\/(\d+)/);
  const id = idMatch ? parseInt(idMatch[1], 10) : null;

  // GET /api/admin/news - 목록 조회
  if (endpoint.includes('?') || endpoint === '/api/admin/news') {
    if (method === 'GET') {
      const url = new URL(endpoint, 'http://localhost');
      const page = parseInt(url.searchParams.get('page') || '1', 10);
      const limit = parseInt(url.searchParams.get('limit') || '10', 10);
      const result = mockStore.getNews(page, limit);
      return { success: true, data: result as T };
    }
  }

  // POST /api/admin/news - 생성
  if (endpoint === '/api/admin/news' && method === 'POST') {
    const news = mockStore.createNews(body as unknown as CreateNewsRequest);
    return { success: true, data: { data: news } as T };
  }

  // GET /api/admin/news/:id - 상세 조회
  if (id && method === 'GET') {
    const news = mockStore.getNewsById(id);
    if (news) {
      return { success: true, data: { data: news } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '게시글을 찾을 수 없습니다.',
      },
    };
  }

  // PUT /api/admin/news/:id - 수정
  if (id && method === 'PUT') {
    const news = mockStore.updateNews({ ...body, id } as unknown as UpdateNewsRequest);
    if (news) {
      return { success: true, data: { data: news } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '게시글을 찾을 수 없습니다.',
      },
    };
  }

  // DELETE /api/admin/news/:id - 삭제
  if (id && method === 'DELETE') {
    const success = mockStore.deleteNews(id);
    if (success) {
      return { success: true, data: { success: true } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '게시글을 찾을 수 없습니다.',
      },
    };
  }

  return {
    success: false,
    error: {
      code: 'NOT_FOUND',
      message: '요청한 API를 찾을 수 없습니다.',
    },
  };
}

/**
 * 갤러리 엔드포인트 처리
 */
function handleGalleryEndpoint<T>(
  endpoint: string,
  method: string,
  body: Record<string, unknown> | null
): ApiResponse<T> {
  // URL에서 ID 추출
  const idMatch = endpoint.match(/\/api\/admin\/gallery\/(\d+)/);
  const id = idMatch ? parseInt(idMatch[1], 10) : null;

  // GET /api/admin/gallery - 목록 조회
  if (endpoint.includes('?') || endpoint === '/api/admin/gallery') {
    if (method === 'GET') {
      const url = new URL(endpoint, 'http://localhost');
      const page = parseInt(url.searchParams.get('page') || '1', 10);
      const limit = parseInt(url.searchParams.get('limit') || '10', 10);
      const category = url.searchParams.get('category') || undefined;
      const result = mockStore.getGallery(page, limit, category);
      return { success: true, data: result as T };
    }
  }

  // POST /api/admin/gallery - 생성
  if (endpoint === '/api/admin/gallery' && method === 'POST') {
    const gallery = mockStore.createGallery(body as unknown as CreateGalleryRequest);
    return { success: true, data: { data: gallery } as T };
  }

  // GET /api/admin/gallery/:id - 상세 조회
  if (id && method === 'GET') {
    const gallery = mockStore.getGalleryById(id);
    if (gallery) {
      return { success: true, data: { data: gallery } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '갤러리를 찾을 수 없습니다.',
      },
    };
  }

  // PUT /api/admin/gallery/:id - 수정
  if (id && method === 'PUT') {
    const gallery = mockStore.updateGallery({ ...body, id } as unknown as UpdateGalleryRequest);
    if (gallery) {
      return { success: true, data: { data: gallery } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '갤러리를 찾을 수 없습니다.',
      },
    };
  }

  // DELETE /api/admin/gallery/:id - 삭제
  if (id && method === 'DELETE') {
    const success = mockStore.deleteGallery(id);
    if (success) {
      return { success: true, data: { success: true } as T };
    }
    return {
      success: false,
      error: {
        code: 'NOT_FOUND',
        message: '갤러리를 찾을 수 없습니다.',
      },
    };
  }

  return {
    success: false,
    error: {
      code: 'NOT_FOUND',
      message: '요청한 API를 찾을 수 없습니다.',
    },
  };
}

/**
 * 실제 API 요청 처리
 * 백엔드 응답 형식: { success: boolean, data?: T, error?: ApiError }
 */
async function realRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Authorization 헤더 자동 추가
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const responseData = await response.json();

    // 백엔드 응답이 이미 { success, data, error } 형식
    if (!response.ok) {
      return {
        success: false,
        error: responseData.error || {
          code: 'SERVER_ERROR',
          message: '서버 오류가 발생했습니다.',
        },
      };
    }

    // 백엔드 응답에서 data 필드 추출
    // 백엔드는 { success: true, data: actualData } 형식으로 응답
    return {
      success: true,
      data: responseData.data as T,
    };
  } catch (error) {
    console.error('API 요청 오류:', error);
    return {
      success: false,
      error: {
        code: 'SERVER_ERROR',
        message: '네트워크 오류가 발생했습니다.',
      },
    };
  }
}

/**
 * API 요청 함수
 * 환경 변수에 따라 Mock API 또는 실제 API를 호출합니다.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  if (USE_MOCK) {
    return mockRequest<T>(endpoint, options);
  }
  return realRequest<T>(endpoint, options);
}

/**
 * Mock 모드 여부 확인
 */
export function isMockMode(): boolean {
  return USE_MOCK;
}
